import { useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import { Phone01, SearchMd } from "@untitledui/icons"
import { toast } from "sonner"

import type { GhlContactRich } from "@/lib/api/ghl-types"
import { useBulkContacts, useGhlContactsRich, useGhlUsers } from "@/lib/api/use-ghl"
import { TextSelect } from "@/components/ui/text-select"
import { cx } from "@/lib/utils/cx"
import { FOLLOW_UP, SEGMENTS } from "./follow-up"
import { OfferDialog } from "./offer-dialog"
import { Avatar, ChannelIcon, DARK_BTN, Empty, INPUT, LIGHT_BTN, TagChip, fmtDate, fmtDateTime, relTime, usd, userName } from "./shared"

// Contacts — the book. Every person with where they are in the follow-up
// (the same words as the rest of the app), what the deal is worth, and
// what happened last. Text, call or open from the row; tag, assign or
// send an offer to many at once.

export function Contacts({ teamId }: { teamId: number | string }) {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [q, setQ] = useState("")
  const seg = params.get("seg") ?? "all"
  const tag = params.get("tag") ?? ""
  const [owner, setOwner] = useState("")
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkTag, setBulkTag] = useState("")
  const [offer, setOffer] = useState(false)

  const { data: everyone = [], isLoading } = useGhlContactsRich(teamId, {})
  const { data: users = [] } = useGhlUsers(teamId)
  const bulk = useBulkContacts(teamId)

  const setParam = (k: string, v: string) => { const next = new URLSearchParams(params); if (v && v !== "all") next.set(k, v); else next.delete(k); setParams(next, { replace: true }) }
  const tags = useMemo(() => [...new Set(everyone.flatMap((c) => c.tags))].sort(), [everyone])
  const counts = useMemo(() => Object.fromEntries(SEGMENTS.map((s) => [s.key, s.states.length ? everyone.filter((c) => s.states.includes(c.followUp)).length : everyone.length])), [everyone])

  const rows = useMemo(() => {
    const states = SEGMENTS.find((s) => s.key === seg)?.states ?? []
    const needle = q.trim().toLowerCase()
    return everyone
      .filter((c) => !states.length || states.includes(c.followUp))
      .filter((c) => !tag || c.tags.includes(tag))
      .filter((c) => !owner || c.assignedTo === owner)
      .filter((c) => !needle || `${c.firstName} ${c.lastName} ${c.phone ?? ""} ${c.email ?? ""} ${c.vehicle ?? ""} ${c.tags.join(" ")}`.toLowerCase().includes(needle))
  }, [everyone, seg, tag, owner, q])

  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const allOn = rows.length > 0 && rows.every((r) => selected.has(r.id))
  const chosen = selected.size ? rows.filter((r) => selected.has(r.id)) : rows

  const applyTag = async () => {
    const t = bulkTag.trim().toLowerCase(); if (!t || !selected.size) return
    await bulk.mutateAsync({ ids: [...selected], addTag: t }); toast(`Tag “${t}” added to ${selected.size}`); setBulkTag(""); setSelected(new Set())
  }
  const assign = async (userId: string) => {
    if (!selected.size) return
    await bulk.mutateAsync({ ids: [...selected], assignedTo: userId || null }); toast(`${selected.size} assigned to ${userName(users, userId)}`); setSelected(new Set())
  }

  return (
    <div>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Follow-up">
        {SEGMENTS.map((s) => (
          <button key={s.key} type="button" role="radio" aria-checked={seg === s.key} onClick={() => setParam("seg", s.key)} className={cx("h-9 rounded-full border px-3 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30", seg === s.key ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50")}>
            {s.label} <span className={cx("ml-1 tabular-nums", seg === s.key ? "text-white/70" : "text-zinc-400")}>{counts[s.key] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <SearchMd className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, vehicle" aria-label="Search contacts" className={cx(INPUT, "w-full pl-9")} />
        </div>
        <TextSelect ariaLabel="Tag" value={tag} onChange={(v) => setParam("tag", v)} options={[{ value: "", label: "All tags" }, ...tags.map((t) => ({ value: t, label: t }))]} />
        <TextSelect ariaLabel="Owner" value={owner} onChange={setOwner} options={[{ value: "", label: "Anyone" }, ...users.map((u) => ({ value: u.id, label: u.name }))]} />
        <p className="ml-auto text-[13px] text-zinc-500">{rows.length} {rows.length === 1 ? "contact" : "contacts"}{selected.size ? ` · ${selected.size} selected` : ""}</p>
        <button type="button" onClick={() => setOffer(true)} disabled={chosen.length === 0} className={LIGHT_BTN}>Send an offer{selected.size ? ` to ${selected.size}` : ""}</button>
      </div>

      {selected.size > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2">
          <span className="text-[13px] font-semibold text-zinc-900">{selected.size} selected</span>
          <span className="mx-1 h-5 w-px bg-zinc-200" aria-hidden />
          <input value={bulkTag} onChange={(e) => setBulkTag(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void applyTag() }} placeholder="Add a tag" aria-label="Tag to add" className={cx(INPUT, "h-9 w-40")} />
          <button type="button" onClick={() => void applyTag()} disabled={!bulkTag.trim() || bulk.isPending} className={cx(DARK_BTN, "h-9")}>Add tag</button>
          <span className="mx-1 h-5 w-px bg-zinc-200" aria-hidden />
          <span className="text-[13px] text-zinc-600">Assign to</span>
          <TextSelect ariaLabel="Assign selected to" value="" onChange={(v) => void assign(v)} options={[{ value: "", label: "Choose…" }, ...users.map((u) => ({ value: u.id, label: u.name }))]} />
          <button type="button" onClick={() => setSelected(new Set())} className={cx(LIGHT_BTN, "ml-auto h-9")}>Clear</button>
        </div>
      )}

      <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-200 bg-white">
        <table className="w-full min-w-[1080px] table-fixed">
          <colgroup>
            <col className="w-11" /><col /><col className="w-[172px]" /><col className="w-[190px]" /><col className="w-[120px]" /><col className="w-[130px]" /><col className="w-[220px]" /><col className="w-[150px]" />
          </colgroup>
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-3 py-2.5"><Check checked={allOn} onChange={() => setSelected(allOn ? new Set() : new Set(rows.map((r) => r.id)))} label="Select all" /></th>
              <Th>Contact</Th><Th>Vehicle</Th><Th>Follow-up</Th><Th>Deal</Th><Th>Owner</Th><Th>Last activity</Th><Th><span className="sr-only">Actions</span></Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && rows.length === 0 && <tr><td colSpan={8}><Empty>Loading contacts…</Empty></td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={8}><Empty>No contacts here.</Empty></td></tr>}
            {rows.map((c) => (
              <Row key={c.id} c={c} checked={selected.has(c.id)} onCheck={() => toggle(c.id)} owner={userName(users, c.assignedTo)} onOpen={() => navigate(`/marketing/contacts/${c.id}`)} />
            ))}
          </tbody>
        </table>
      </div>

      <OfferDialog open={offer} teamId={teamId} recipients={chosen.map((c) => ({ id: c.id, firstName: c.firstName, phone: c.phone, smsBlocked: !c.phone || c.dndSettings.SMS.status === "active" }))} onClose={() => setOffer(false)} />
    </div>
  )
}

function followUpLine(c: GhlContactRich): string | null {
  if (c.followUp === "waiting" && c.lastMessage) return `Wrote ${relTime(c.lastMessage.date)}`
  if (c.followUp === "booked" && c.bookedAt) return fmtDateTime(c.bookedAt)
  if (c.followUp === "to_call" && c.nextTouchAt) return `${c.nextTouchTitle ?? "Call"} · ${new Date(c.nextTouchAt).getTime() < Date.now() ? "overdue" : fmtDate(c.nextTouchAt)}`
  if (c.followUp === "following" && c.nextTouchAt) return `Next: ${c.nextTouchTitle ?? "follow-up"} · ${fmtDate(c.nextTouchAt)}`
  if (c.followUp === "to_confirm") return "Waiting for your confirm"
  return null
}

function Row({ c, checked, onCheck, owner, onOpen }: { c: GhlContactRich; checked: boolean; onCheck: () => void; owner: string; onOpen: () => void }) {
  const name = `${c.firstName} ${c.lastName}`.trim()
  const fu = FOLLOW_UP[c.followUp]
  const optOut = c.dndSettings.SMS.status === "active" || c.dndSettings.Email.status === "active"
  const line = followUpLine(c)
  return (
    <tr onClick={onOpen} className={cx("cursor-pointer border-t border-zinc-100 hover:bg-zinc-50/70", checked && "bg-zinc-50")}>
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}><Check checked={checked} onChange={onCheck} label={`Select ${name}`} /></td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          <Avatar name={name} />
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 truncate text-[14px] font-semibold text-zinc-900">
              {name}
              {c.tags.includes("repeat") && <span className="rounded bg-emerald-50 px-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Repeat</span>}
              {optOut && <span className="rounded bg-red-50 px-1 text-[10px] font-bold uppercase tracking-wide text-red-700">Opted out</span>}
            </p>
            <p className="truncate text-[12.5px] text-zinc-500">{[c.phone, c.email].filter(Boolean).join(" · ") || "No contact info"}</p>
          </div>
        </div>
      </td>
      <td className="truncate px-3 py-2.5 text-[13px] text-zinc-700">{c.vehicle ?? <span className="text-zinc-400">—</span>}</td>
      <td className="px-3 py-2.5">
        <p className={cx("flex items-center gap-1.5 text-[13px] font-medium", fu.text)}><span className={cx("size-2 rounded-full", fu.dot)} aria-hidden />{fu.label}</p>
        {line && <p className="truncate text-[12px] text-zinc-500">{line}</p>}
      </td>
      <td className="px-3 py-2.5">
        {c.dealValue != null ? (
          <>
            <p className={cx("text-[13.5px] font-semibold tabular-nums", c.dealStatus === "won" ? "text-emerald-700" : c.dealStatus === "lost" ? "text-zinc-400 line-through" : "text-zinc-900")}>{usd(c.dealValue)}</p>
            <p className="truncate text-[12px] text-zinc-500">{c.dealStatus === "open" ? c.stageName : c.dealStatus}</p>
          </>
        ) : <span className="text-[13px] text-zinc-400">—</span>}
      </td>
      <td className="truncate px-3 py-2.5 text-[13px] text-zinc-700">{owner}</td>
      <td className="px-3 py-2.5">
        {c.lastMessage ? (
          <div className="flex items-center gap-2">
            <ChannelIcon type={c.lastMessage.type} className="shrink-0 text-zinc-400" />
            <div className="min-w-0">
              <p className={cx("truncate text-[13px]", c.unreadCount > 0 ? "font-semibold text-zinc-900" : "text-zinc-700")}>{c.lastMessage.direction === "inbound" ? `${c.firstName}: ` : "You: "}{c.lastMessage.body}</p>
              <p className="text-[11.5px] text-zinc-400">{relTime(c.lastMessage.date)}</p>
            </div>
          </div>
        ) : <span className="text-[13px] text-zinc-400">No messages yet</span>}
      </td>
      <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          {c.phone && <a href={`tel:${c.phone.replace(/[^\d+]/g, "")}`} className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-[12.5px] font-semibold text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"><Phone01 className="size-3.5" aria-hidden />Call</a>}
          <Link to={`/marketing/contacts/${c.id}?compose=1`} className="inline-flex h-9 items-center rounded-md px-2 text-[12.5px] font-semibold text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900">Text</Link>
          <Link to={`/marketing/contacts/${c.id}`} className="inline-flex h-9 items-center rounded-md px-2 text-[12.5px] font-semibold text-zinc-900 hover:bg-zinc-100">Open</Link>
        </div>
      </td>
    </tr>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th scope="col" className="px-3 py-2.5 text-left text-[10.5px] font-bold uppercase tracking-[0.12em] text-zinc-500">{children}</th>
}

export function Check({ checked, onChange, label }: { checked: boolean; onChange: () => void; label: string }) {
  return (
    <button type="button" role="checkbox" aria-checked={checked} aria-label={label} onClick={onChange} className="grid size-8 place-items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30">
      <span aria-hidden className={cx("grid size-[18px] place-items-center rounded-[4px] border-[1.5px] text-white transition-colors", checked ? "border-zinc-900 bg-zinc-900" : "border-zinc-300 bg-white")}>
        {checked && <svg viewBox="0 0 12 12" className="size-3" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 6.5l2.5 2.5 4.5-5" /></svg>}
      </span>
    </button>
  )
}
